import { useEffect, useState, useRef, useCallback } from 'preact/hooks';
import { useAuth, UserButton } from '@clerk/clerk-react';
import { api, AdminDashboardData, AdminUserList } from '../api/client';
import { SkeletonStyles } from '../components/Skeleton';

type AdminTab = 'metrics' | 'users';

export function Admin() {
	const { getToken } = useAuth();
	const [tab, setTab] = useState<AdminTab>('metrics');
	const [data, setData] = useState<AdminDashboardData | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [forbidden, setForbidden] = useState(false);

	// User management state
	const [userList, setUserList] = useState<AdminUserList | null>(null);
	const [usersLoading, setUsersLoading] = useState(false);
	const [usersError, setUsersError] = useState<string | null>(null);
	const [userSearch, setUserSearch] = useState('');
	const [userTierFilter, setUserTierFilter] = useState<string>('');
	const [userPage, setUserPage] = useState(1);
	const [tierChanging, setTierChanging] = useState<string | null>(null);
	const [tierConfirm, setTierConfirm] = useState<{ userId: string; email: string; currentTier: string; newTier: 'free' | 'pro' } | null>(null);
	const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		loadData();
	}, []);

	async function loadData() {
		try {
			setLoading(true);
			setError(null);
			const token = await getToken();
			if (!token) return;
			const result = await api.getAdminDashboard(token);
			setData(result);
		} catch (err) {
			const msg = err instanceof Error ? err.message : 'Failed to load';
			if (msg === 'Forbidden') {
				setForbidden(true);
			} else {
				setError(msg);
			}
		} finally {
			setLoading(false);
		}
	}

	const loadUsers = useCallback(async (search?: string, tier?: string, page?: number) => {
		try {
			setUsersLoading(true);
			setUsersError(null);
			const token = await getToken();
			if (!token) return;
			const result = await api.getAdminUsers(token, {
				search: search || undefined,
				tier: tier || undefined,
				page: page || 1,
				limit: 25,
			});
			setUserList(result);
		} catch (err) {
			setUsersError(err instanceof Error ? err.message : 'Failed to load users');
		} finally {
			setUsersLoading(false);
		}
	}, [getToken]);

	// Load users when switching to users tab
	useEffect(() => {
		if (tab === 'users' && !userList && !usersLoading) {
			loadUsers(userSearch, userTierFilter, userPage);
		}
	}, [tab]);

	function handleSearchChange(value: string) {
		setUserSearch(value);
		setUserPage(1);
		if (searchTimeout.current) clearTimeout(searchTimeout.current);
		searchTimeout.current = setTimeout(() => {
			loadUsers(value, userTierFilter, 1);
		}, 300);
	}

	function handleTierFilterChange(tier: string) {
		setUserTierFilter(tier);
		setUserPage(1);
		loadUsers(userSearch, tier, 1);
	}

	function handlePageChange(page: number) {
		setUserPage(page);
		loadUsers(userSearch, userTierFilter, page);
	}

	async function handleTierChange() {
		if (!tierConfirm) return;
		try {
			setTierChanging(tierConfirm.userId);
			const token = await getToken();
			if (!token) return;
			await api.updateAdminUserTier(token, tierConfirm.userId, tierConfirm.newTier);
			// Refresh user list
			await loadUsers(userSearch, userTierFilter, userPage);
			setTierConfirm(null);
		} catch (err) {
			setUsersError(err instanceof Error ? err.message : 'Failed to update tier');
		} finally {
			setTierChanging(null);
		}
	}

	async function handleGrantTrial(userId: string, email: string) {
		const input = window.prompt(`Grant trial pro access to ${email} for how many days? (1–365)`, '30');
		if (input === null) return;
		const days = parseInt(input, 10);
		if (!Number.isFinite(days) || days <= 0 || days > 365) {
			setUsersError('Invalid trial length. Must be 1–365 days.');
			return;
		}
		try {
			setTierChanging(userId);
			const token = await getToken();
			if (!token) return;
			await api.grantAdminUserTrial(token, userId, days);
			await loadUsers(userSearch, userTierFilter, userPage);
		} catch (err) {
			setUsersError(err instanceof Error ? err.message : 'Failed to grant trial');
		} finally {
			setTierChanging(null);
		}
	}

	function formatDate(timestamp: number): string {
		return new Date(timestamp).toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			hour: 'numeric',
			minute: '2-digit',
		});
	}

	function formatCurrency(amount: number): string {
		return `$${amount.toFixed(2)}`;
	}

	function formatPercent(rate: number): string {
		return `${(rate * 100).toFixed(1)}%`;
	}

	function formatRole(role: string): string {
		return role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
	}

	if (forbidden) {
		return (
			<div class="page">
				<style>{adminStyles}</style>
				<main class="admin-main">
					<div class="container">
						<div class="forbidden-state">
							<h1 class="t-title">Access Denied</h1>
							<p class="t-body text-muted">You don't have admin access.</p>
							<a href="/dashboard" class="btn btn-secondary">Back to Dashboard</a>
						</div>
					</div>
				</main>
			</div>
		);
	}

	if (loading) {
		return (
			<div class="page">
				<header class="admin-header">
					<div class="container">
						<nav class="admin-nav">
							<a href="/" class="logo">
								<span class="logo-text">pxtxt</span>
							</a>
							<div class="nav-actions">
								<UserButton afterSignOutUrl="/" />
							</div>
						</nav>
					</div>
				</header>
				<main class="admin-main">
					<div class="container">
						<SkeletonStyles />
						<AdminSkeleton />
					</div>
				</main>
				<style>{adminStyles}</style>
			</div>
		);
	}

	if (error) {
		return (
			<div class="page">
				<style>{adminStyles}</style>
				<header class="admin-header">
					<div class="container">
						<nav class="admin-nav">
							<a href="/" class="logo">
								<span class="logo-text">pxtxt</span>
							</a>
							<div class="nav-actions">
								<UserButton afterSignOutUrl="/" />
							</div>
						</nav>
					</div>
				</header>
				<main class="admin-main">
					<div class="container">
						<div class="error-banner">
							<span>{error}</span>
							<button class="btn btn-secondary btn-sm" onClick={loadData}>Retry</button>
						</div>
					</div>
				</main>
			</div>
		);
	}

	if (!data) return null;

	const { overview, engagements, users, costs, alerts } = data;

	return (
		<div class="page">
			<style>{adminStyles}</style>

			<header class="admin-header">
				<div class="container">
					<nav class="admin-nav">
						<a href="/" class="logo">
							<span class="logo-text">pxtxt</span>
						</a>
						<div class="nav-actions">
							<a href="/dashboard" class="nav-link">
								<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
									<path d="M15 18l-6-6 6-6" />
								</svg>
								Dashboard
							</a>
							<UserButton afterSignOutUrl="/" />
						</div>
					</nav>
				</div>
			</header>

			<main class="admin-main">
				<div class="container">

					<div class="admin-welcome">
						<h1 class="t-title">Admin Dashboard</h1>
						<p class="t-body text-muted">Operational metrics and system health.</p>
					</div>

					{/* Tab Navigation */}
					<div class="admin-tabs">
						<button
							class={`admin-tab ${tab === 'metrics' ? 'admin-tab--active' : ''}`}
							onClick={() => setTab('metrics')}
						>
							Metrics
						</button>
						<button
							class={`admin-tab ${tab === 'users' ? 'admin-tab--active' : ''}`}
							onClick={() => setTab('users')}
						>
							Users{data ? ` (${data.overview.totalUsers})` : ''}
						</button>
					</div>

					{/* ── Metrics Tab ── */}
					{tab === 'metrics' && (<>

					{/* Alerts */}
					{alerts.length > 0 && alerts[0].category !== 'system' && (
						<div class="alerts-section">
							{alerts.map((alert, i) => (
								<div key={i} class={`alert-card alert-${alert.type}`}>
									<div class="alert-icon">
										{alert.type === 'error' && (
											<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
												<circle cx="12" cy="12" r="10" />
												<line x1="15" y1="9" x2="9" y2="15" />
												<line x1="9" y1="9" x2="15" y2="15" />
											</svg>
										)}
										{alert.type === 'warning' && (
											<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
												<path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
												<line x1="12" y1="9" x2="12" y2="13" />
												<line x1="12" y1="17" x2="12.01" y2="17" />
											</svg>
										)}
										{alert.type === 'info' && (
											<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
												<circle cx="12" cy="12" r="10" />
												<line x1="12" y1="16" x2="12" y2="12" />
												<line x1="12" y1="8" x2="12.01" y2="8" />
											</svg>
										)}
									</div>
									<span class="alert-message">{alert.message}</span>
									<span class="badge badge-default">{alert.category}</span>
								</div>
							))}
						</div>
					)}

					{/* Overview Stats */}
					<section class="admin-section">
						<h2 class="t-subtitle">Overview</h2>
						<div class="stats-grid stats-grid-5">
							<div class="stat-card">
								<p class="t-label">Users</p>
								<div class="stat-value t-title--sm">{overview.totalUsers}</div>
							</div>
							<div class="stat-card">
								<p class="t-label">Pro Users</p>
								<div class="stat-value t-title--sm">{overview.proUsers}</div>
							</div>
							<div class="stat-card">
								<p class="t-label">Engagements</p>
								<div class="stat-value t-title--sm">{overview.totalEngagements}</div>
							</div>
							<div class="stat-card">
								<p class="t-label">MTD Cost (est.)</p>
								<div class="stat-value t-title--sm">{formatCurrency(costs.mtdEstimated)}</div>
							</div>
							<div class="stat-card">
								<p class="t-label">Published Profiles</p>
								<div class="stat-value t-title--sm">{overview.publishedProfiles}</div>
							</div>
							<div class="stat-card">
								<p class="t-label">Narratives</p>
								<div class="stat-value t-title--sm">{overview.totalNarratives || 0}</div>
								{(overview.totalNarratives || 0) > 0 && (
									<span class="stat-detail"> ({overview.baselineNarratives || 0} baseline, {overview.fullNarratives || 0} full)</span>
								)}
							</div>
						</div>
					</section>

					{/* Engagements */}
					<section class="admin-section">
						<h2 class="t-subtitle">Engagements</h2>
						<div class="stats-grid stats-grid-4">
							<div class="stat-card">
								<p class="t-label">Total</p>
								<div class="stat-value t-title--sm">{engagements.total}</div>
							</div>
							<div class="stat-card">
								<p class="t-label">This Month</p>
								<div class="stat-value t-title--sm">{engagements.mtd}</div>
							</div>
							<div class="stat-card">
								<p class="t-label">Last 7 Days</p>
								<div class="stat-value t-title--sm">{engagements.last7d}</div>
							</div>
							<div class="stat-card">
								<p class="t-label">Avg Messages</p>
								<div class="stat-value t-title--sm">{engagements.avgMessageCount}</div>
							</div>
						</div>

						<div class="metrics-row">
							<div class="metric-block">
								<p class="t-label">Contact Rate</p>
								<p class="metric-value">{formatPercent(engagements.contactRequestRate)}</p>
							</div>
							<div class="metric-block">
								<p class="t-label">Debrief Coverage</p>
								<p class="metric-value">{formatPercent(engagements.debriefCoverage)}</p>
							</div>
						</div>

						<div class="distributions-row">
							<div class="distribution-card card">
								<h3 class="t-label">Visitor Quality</h3>
								<DistributionBars
									data={engagements.qualityDistribution}
									colorMap={{ high: 'var(--color-success)', medium: 'var(--color-warning)', low: 'var(--color-text-subtle)', unknown: 'var(--color-border)' }}
								/>
							</div>
							<div class="distribution-card card">
								<h3 class="t-label">Visitor Role</h3>
								<DistributionBars
									data={engagements.roleDistribution}
									formatLabel={formatRole}
								/>
							</div>
						</div>

						{engagements.recent.length > 0 && (
							<div class="recent-engagements">
								<h3 class="t-label">Recent Engagements</h3>
								<div class="engagement-table">
									<div class="table-header">
										<span>Time</span>
										<span>Tenant</span>
										<span>Role</span>
										<span>Quality</span>
										<span>Msgs</span>
										<span>Contact</span>
										<span>Debrief</span>
									</div>
									{engagements.recent.map(e => (
										<div key={e.id} class="table-row">
											<span class="t-caption">{formatDate(e.lastActiveAt)}</span>
											<span class="font-mono">{e.tenantSlug}</span>
											<span>{e.visitorRole ? formatRole(e.visitorRole) : '—'}</span>
											<span>
												<span class={`quality-dot quality-${e.visitorQuality}`} />
												{' '}{e.visitorQuality}
											</span>
											<span>{e.messageCount}</span>
											<span>{e.contactRequested ? 'Yes' : '—'}</span>
											<span>{e.hasDebrief ? 'Yes' : '—'}</span>
										</div>
									))}
								</div>
							</div>
						)}
					</section>

					{/* Users */}
					<section class="admin-section">
						<h2 class="t-subtitle">Users</h2>
						<div class="stats-grid stats-grid-3">
							<div class="stat-card">
								<p class="t-label">Total Users</p>
								<div class="stat-value t-title--sm">{users.total}</div>
							</div>
							<div class="stat-card">
								<p class="t-label">Email Verification</p>
								<div class="stat-value t-title--sm">{formatPercent(users.emailVerificationRate)}</div>
							</div>
							<div class="stat-card">
								<p class="t-label">Profiles</p>
								<div class="stat-value t-title--sm">
									{overview.totalProfiles}
									<span class="stat-detail"> ({overview.chatEnabledProfiles} chat)</span>
								</div>
							</div>
						</div>

						<div class="distributions-row">
							<div class="distribution-card card">
								<h3 class="t-label">Tier Distribution</h3>
								<DistributionBars
									data={users.tierDistribution}
									colorMap={{ pro: 'var(--color-primary)', free: 'var(--color-border)' }}
								/>
							</div>
							<div class="distribution-card card">
								<h3 class="t-label">Interview Sessions</h3>
								<DistributionBars
									data={users.interviewSessions}
									colorMap={{ completed: 'var(--color-success)', in_progress: 'var(--color-warning)', abandoned: 'var(--color-text-subtle)' }}
									formatLabel={formatRole}
								/>
							</div>
							<div class="distribution-card card">
								<h3 class="t-label">Claims</h3>
								<DistributionBars
									data={users.claims}
									colorMap={{ approved: 'var(--color-success)', pending: 'var(--color-warning)', rejected: 'var(--color-error)' }}
									formatLabel={formatRole}
								/>
							</div>
						</div>
					</section>

					{/* Costs */}
					<section class="admin-section">
						<h2 class="t-subtitle">Cost Estimates</h2>
						<div class="stats-grid stats-grid-4">
							<div class="stat-card">
								<p class="t-label">All-Time</p>
								<div class="stat-value t-title--sm">{formatCurrency(costs.totalEstimated)}</div>
							</div>
							<div class="stat-card">
								<p class="t-label">This Month</p>
								<div class="stat-value t-title--sm">{formatCurrency(costs.mtdEstimated)}</div>
							</div>
							<div class="stat-card">
								<p class="t-label">Avg / Engagement</p>
								<div class="stat-value t-title--sm">{formatCurrency(costs.avgPerEngagement)}</div>
							</div>
							<div class="stat-card">
								<p class="t-label">Avg / Session</p>
								<div class="stat-value t-title--sm">{formatCurrency(costs.avgPerSession)}</div>
							</div>
						</div>

						<div class="distributions-row">
							<div class="distribution-card card">
								<h3 class="t-label">Visitor-Side (Representation)</h3>
								<div class="cost-breakdown">
									<div class="cost-line">
										<span class="t-caption">Chat messages</span>
										<span class="t-caption font-mono">{formatCurrency(costs.breakdown.representation)}</span>
									</div>
									<div class="cost-line">
										<span class="t-caption">Debriefs</span>
										<span class="t-caption font-mono">{formatCurrency(costs.breakdown.debriefs)}</span>
									</div>
								</div>
							</div>
							<div class="distribution-card card">
								<h3 class="t-label">Professional-Side (Sessions)</h3>
								<div class="cost-breakdown">
									<div class="cost-line">
										<span class="t-caption">Extraction</span>
										<span class="t-caption font-mono">{formatCurrency(costs.breakdown.extraction)}</span>
									</div>
									<div class="cost-line">
										<span class="t-caption">Artifacts</span>
										<span class="t-caption font-mono">{formatCurrency(costs.breakdown.artifacts)}</span>
									</div>
								</div>
							</div>
						</div>
						<p class="t-caption text-muted cost-note">
							Estimated from message counts × per-operation cost (Sonnet 4: $3/MTok in, $15/MTok out). Actual token usage tracking planned.
						</p>
					</section>

					{/* System Health */}
					{alerts.length > 0 && alerts[0].category === 'system' && (
						<section class="admin-section">
							<h2 class="t-subtitle">System Health</h2>
							<div class="system-ok card">
								<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" stroke-width="2">
									<path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
									<polyline points="22 4 12 14.01 9 11.01" />
								</svg>
								<span class="t-body">All systems operating normally</span>
							</div>
						</section>
					)}

					</>)}

					{/* ── Users Tab ── */}
					{tab === 'users' && (
						<UserManagement
							users={userList}
							loading={usersLoading}
							error={usersError}
							search={userSearch}
							tierFilter={userTierFilter}
							page={userPage}
							tierChanging={tierChanging}
							tierConfirm={tierConfirm}
							onSearchChange={handleSearchChange}
							onTierFilterChange={handleTierFilterChange}
							onPageChange={handlePageChange}
							onTierConfirm={setTierConfirm}
							onTierChange={handleTierChange}
							onTierCancel={() => setTierConfirm(null)}
							onGrantTrial={handleGrantTrial}
							formatDate={formatDate}
						/>
					)}

				</div>
			</main>
		</div>
	);
}

// Distribution bar component
function DistributionBars({
	data,
	colorMap,
	formatLabel,
}: {
	data: Record<string, number>;
	colorMap?: Record<string, string>;
	formatLabel?: (key: string) => string;
}) {
	const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
	const total = entries.reduce((sum, [, v]) => sum + v, 0);

	if (total === 0) {
		return <p class="t-caption text-muted">No data</p>;
	}

	return (
		<div class="dist-bars">
			{entries.map(([key, count]) => {
				const pct = (count / total) * 100;
				const color = colorMap?.[key] || 'var(--color-primary)';
				const label = formatLabel ? formatLabel(key) : key;
				return (
					<div key={key} class="dist-row">
						<div class="dist-label">
							<span class="t-caption">{label}</span>
							<span class="t-caption font-mono">{count}</span>
						</div>
						<div class="dist-track">
							<div
								class="dist-fill"
								style={{ width: `${Math.max(pct, 2)}%`, background: color }}
							/>
						</div>
					</div>
				);
			})}
		</div>
	);
}

// User management component
function UserManagement({
	users, loading, error, search, tierFilter, page, tierChanging, tierConfirm,
	onSearchChange, onTierFilterChange, onPageChange, onTierConfirm, onTierChange, onTierCancel, onGrantTrial, formatDate,
}: {
	users: AdminUserList | null;
	loading: boolean;
	error: string | null;
	search: string;
	tierFilter: string;
	page: number;
	tierChanging: string | null;
	tierConfirm: { userId: string; email: string; currentTier: string; newTier: 'free' | 'pro' } | null;
	onSearchChange: (v: string) => void;
	onTierFilterChange: (v: string) => void;
	onPageChange: (p: number) => void;
	onTierConfirm: (v: { userId: string; email: string; currentTier: string; newTier: 'free' | 'pro' } | null) => void;
	onTierChange: () => void;
	onTierCancel: () => void;
	onGrantTrial: (userId: string, email: string) => void;
	formatDate: (ts: number) => string;
}) {
	const totalPages = users ? Math.ceil(users.total / users.limit) : 0;

	return (
		<section class="admin-section">
			{/* Tier Change Confirmation Dialog */}
			{tierConfirm && (
				<div class="tier-confirm-overlay" onClick={onTierCancel}>
					<div class="tier-confirm-dialog" onClick={e => e.stopPropagation()}>
						<h3 class="t-subtitle">Change User Tier</h3>
						<p class="t-body">
							Change <strong>{tierConfirm.email}</strong> from{' '}
							<span class={`badge badge-${tierConfirm.currentTier === 'pro' ? 'success' : 'default'}`}>
								{tierConfirm.currentTier}
							</span>{' '}
							to{' '}
							<span class={`badge badge-${tierConfirm.newTier === 'pro' ? 'success' : 'default'}`}>
								{tierConfirm.newTier}
							</span>?
						</p>
						<p class="t-caption text-muted">
							This is an admin override. It does not affect the user's Stripe subscription.
						</p>
						<div class="tier-confirm-actions">
							<button class="btn btn-secondary btn-sm" onClick={onTierCancel}>Cancel</button>
							<button
								class="btn btn-primary btn-sm"
								onClick={onTierChange}
								disabled={tierChanging !== null}
							>
								{tierChanging ? 'Updating...' : 'Confirm'}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Controls */}
			<div class="user-controls">
				<div class="user-search-wrap">
					<svg class="user-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
						<circle cx="11" cy="11" r="8" />
						<line x1="21" y1="21" x2="16.65" y2="16.65" />
					</svg>
					<input
						type="text"
						class="user-search"
						placeholder="Search by email or ID..."
						value={search}
						onInput={e => onSearchChange((e.target as HTMLInputElement).value)}
					/>
				</div>
				<div class="user-filters">
					<select
						class="user-tier-filter"
						value={tierFilter}
						onChange={e => onTierFilterChange((e.target as HTMLSelectElement).value)}
					>
						<option value="">All tiers</option>
						<option value="free">Free</option>
						<option value="pro">Pro</option>
					</select>
				</div>
			</div>

			{error && (
				<div class="error-banner">
					<span>{error}</span>
				</div>
			)}

			{loading && !users && (
				<div class="user-loading">
					<span class="spinner" /> Loading users...
				</div>
			)}

			{users && (
				<>
					<div class="user-summary t-caption text-muted">
						{users.total} user{users.total !== 1 ? 's' : ''}{search ? ` matching "${search}"` : ''}{tierFilter ? ` (${tierFilter})` : ''}
					</div>

					<div class="user-table">
						<div class="user-table-header">
							<span>Email</span>
							<span>Tier</span>
							<span>Verified</span>
							<span>Sessions</span>
							<span>Claims</span>
							<span>Cost</span>
							<span>Profile</span>
							<span>Joined</span>
							<span>Actions</span>
						</div>
						{users.users.length === 0 && (
							<div class="user-table-empty">
								<p class="t-body text-muted">No users found.</p>
							</div>
						)}
						{users.users.map(user => (
							<div key={user.id} class={`user-table-row ${tierChanging === user.id ? 'user-row-updating' : ''}`}>
								<span class="user-email" title={user.id}>
									{user.email || <em class="text-muted">No email</em>}
								</span>
								<span>
									<span class={`badge badge-${user.tier === 'pro' ? 'success' : 'default'}`}>
										{user.tier}
									</span>
									{user.stripeCustomerId && (
										<span class="stripe-indicator" title="Has Stripe subscription">$</span>
									)}
									{user.tier === 'pro' && user.proExpiresAt !== null && (() => {
										const msLeft = user.proExpiresAt - Date.now();
										if (msLeft > 0) {
											const days = Math.ceil(msLeft / 86400000);
											return (
												<span class="trial-chip" title={`Trial ends ${new Date(user.proExpiresAt).toLocaleString()}`}>
													trial · {days}d
												</span>
											);
										}
										return (
											<span class="trial-chip trial-chip-expired" title={`Expired ${new Date(user.proExpiresAt).toLocaleString()}`}>
												trial · expired
											</span>
										);
									})()}
								</span>
								<span>{user.emailVerified ? 'Yes' : <span class="text-muted">No</span>}</span>
								<span>{user.sessionCount}</span>
								<span>{user.approvedClaims}</span>
								<span class="font-mono">{user.estimatedCost > 0 ? `$${user.estimatedCost.toFixed(2)}` : '—'}</span>
								<span>
									{user.hasPublicProfile && user.profileSlug ? (
										<a href={`/b/${user.profileSlug}`} class="profile-link" target="_blank" rel="noopener noreferrer">
											{user.profileSlug}
										</a>
									) : user.profileSlug ? (
										<span class="text-muted">Draft</span>
									) : (
										<span class="text-muted">—</span>
									)}
								</span>
								<span class="t-caption">{formatDate(user.createdAt)}</span>
								<span class="user-actions">
									<button
										class="btn btn-xs btn-tier-toggle"
										onClick={() => onTierConfirm({
											userId: user.id,
											email: user.email,
											currentTier: user.tier,
											newTier: user.tier === 'pro' ? 'free' : 'pro',
										})}
										disabled={tierChanging !== null}
										title={user.tier === 'pro' ? 'Downgrade to Free (nulls any trial expiry)' : 'Upgrade to indefinite Pro'}
									>
										{user.tier === 'pro' ? 'Downgrade' : 'Upgrade'}
									</button>
									<button
										class="btn btn-xs btn-trial-grant"
										onClick={() => onGrantTrial(user.id, user.email)}
										disabled={tierChanging !== null}
										title="Grant or extend a time-boxed pro trial"
									>
										Trial
									</button>
								</span>
							</div>
						))}
					</div>

					{/* Pagination */}
					{totalPages > 1 && (
						<div class="user-pagination">
							<button
								class="btn btn-secondary btn-sm"
								disabled={page <= 1}
								onClick={() => onPageChange(page - 1)}
							>
								Prev
							</button>
							<span class="t-caption">
								Page {page} of {totalPages}
							</span>
							<button
								class="btn btn-secondary btn-sm"
								disabled={page >= totalPages}
								onClick={() => onPageChange(page + 1)}
							>
								Next
							</button>
						</div>
					)}
				</>
			)}
		</section>
	);
}

// Admin skeleton loader
function AdminSkeleton() {
	return (
		<div class="admin-skeleton">
			<div class="skel-title skeleton" style={{ width: '200px', height: '32px' }} />
			<div class="skel-subtitle skeleton" style={{ width: '280px', height: '20px', marginTop: '8px' }} />
			<div class="stats-grid stats-grid-5" style={{ marginTop: '2rem' }}>
				{[...Array(5)].map((_, i) => (
					<div key={i} class="stat-card">
						<div class="skeleton" style={{ width: '60px', height: '12px', marginBottom: '8px' }} />
						<div class="skeleton" style={{ width: '40px', height: '24px' }} />
					</div>
				))}
			</div>
			<div style={{ marginTop: '2rem' }}>
				<div class="skeleton" style={{ width: '140px', height: '20px', marginBottom: '1rem' }} />
				<div class="stats-grid stats-grid-4">
					{[...Array(4)].map((_, i) => (
						<div key={i} class="stat-card">
							<div class="skeleton" style={{ width: '80px', height: '12px', marginBottom: '8px' }} />
							<div class="skeleton" style={{ width: '50px', height: '24px' }} />
						</div>
					))}
				</div>
			</div>
		</div>
	);
}

const adminStyles = `
	.admin-header {
		background: var(--color-bg-elevated);
		border-bottom: 1px solid var(--color-border);
		position: sticky;
		top: 0;
		z-index: 100;
	}

	.admin-nav {
		display: flex;
		align-items: center;
		justify-content: space-between;
		height: 56px;
	}

	.nav-actions {
		display: flex;
		align-items: center;
		gap: 1rem;
	}

	.nav-link {
		display: flex;
		align-items: center;
		gap: 0.375rem;
		padding: 0.5rem 0.75rem;
		font-size: 0.875rem;
		font-weight: 500;
		color: var(--color-text-muted);
		text-decoration: none;
		border-radius: var(--radius-md);
		transition: color 150ms, background 150ms;
	}

	.nav-link:hover {
		color: var(--color-text);
		background: var(--color-bg-hover);
	}

	.admin-main {
		padding: 2rem 0 4rem;
	}

	.admin-welcome {
		margin-bottom: 2rem;
	}

	.admin-welcome .t-title {
		margin-bottom: 0.5rem;
	}

	.forbidden-state {
		text-align: center;
		padding: 4rem 2rem;
	}

	.forbidden-state .t-title {
		margin-bottom: 0.5rem;
	}

	.forbidden-state .btn {
		margin-top: 1.5rem;
	}

	.error-banner {
		display: flex;
		align-items: center;
		justify-content: space-between;
		background: rgba(222, 46, 33, 0.1);
		border: 1px solid var(--color-error);
		color: var(--color-error);
		padding: 0.875rem 1rem;
		border-radius: var(--radius-md);
		margin-bottom: 1.5rem;
		font-size: 0.875rem;
	}

	/* Alerts */
	.alerts-section {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
		margin-bottom: 2rem;
	}

	.alert-card {
		display: flex;
		align-items: center;
		gap: 0.75rem;
		padding: 0.75rem 1rem;
		border-radius: var(--radius-md);
		font-size: 0.875rem;
		line-height: 1.4;
	}

	.alert-icon {
		flex-shrink: 0;
		display: flex;
		align-items: center;
	}

	.alert-message {
		flex: 1;
	}

	.alert-info {
		background: rgba(8, 117, 225, 0.08);
		border: 1px solid rgba(8, 117, 225, 0.3);
		color: var(--color-primary);
	}

	.alert-warning {
		background: rgba(245, 166, 35, 0.1);
		border: 1px solid rgba(245, 166, 35, 0.4);
		color: var(--color-warning-text);
	}

	.alert-error {
		background: rgba(222, 46, 33, 0.1);
		border: 1px solid var(--color-error);
		color: var(--color-error);
	}

	/* Sections */
	.admin-section {
		margin-bottom: 2.5rem;
	}

	.admin-section .t-subtitle {
		margin-bottom: 1rem;
	}

	/* Stats grids */
	.stats-grid {
		display: grid;
		gap: 0.75rem;
		margin-bottom: 1rem;
	}

	.stats-grid-3 {
		grid-template-columns: repeat(3, 1fr);
	}

	.stats-grid-4 {
		grid-template-columns: repeat(4, 1fr);
	}

	.stats-grid-5 {
		grid-template-columns: repeat(5, 1fr);
	}

	.stat-card {
		background: var(--color-bg-elevated);
		border: 1px solid var(--color-border);
		border-radius: 2px;
		padding: 1rem 1.25rem;
	}

	.stat-card .t-label {
		margin-bottom: 0.375rem;
	}

	.stat-value {
		display: flex;
		align-items: baseline;
	}

	.stat-detail {
		font-size: 0.75rem;
		font-weight: 400;
		color: var(--color-text-muted);
		margin-left: 0.25rem;
	}

	/* Metrics row */
	.metrics-row {
		display: flex;
		gap: 2rem;
		margin-bottom: 1rem;
	}

	.metric-block .t-label {
		margin-bottom: 0.25rem;
	}

	.metric-value {
		font-family: var(--font-display);
		font-size: 1.25rem;
		font-weight: 300;
		color: var(--color-text);
		margin: 0;
	}

	/* Distribution cards */
	.distributions-row {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
		gap: 0.75rem;
		margin-top: 1rem;
	}

	.distribution-card {
		padding: 1rem 1.25rem;
	}

	.distribution-card .t-label {
		margin-bottom: 0.75rem;
	}

	.dist-bars {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
	}

	.dist-row {
		display: flex;
		flex-direction: column;
		gap: 0.25rem;
	}

	.dist-label {
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	.dist-track {
		height: 6px;
		background: var(--color-bg);
		border-radius: 3px;
		overflow: hidden;
	}

	.dist-fill {
		height: 100%;
		border-radius: 3px;
		transition: width 300ms ease;
	}

	/* Cost section */
	.cost-breakdown {
		display: flex;
		flex-direction: column;
		gap: 0.25rem;
		margin-top: 0.25rem;
	}

	.cost-line {
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	.cost-note {
		margin-top: 0.5rem;
	}

	/* Recent engagements table */
	.recent-engagements {
		margin-top: 1.5rem;
	}

	.recent-engagements .t-label {
		margin-bottom: 0.75rem;
	}

	.engagement-table {
		background: var(--color-bg-elevated);
		border: 1px solid var(--color-border);
		border-radius: 2px;
		overflow: hidden;
	}

	.table-header,
	.table-row {
		display: grid;
		grid-template-columns: 1.5fr 1fr 1fr 1fr 0.5fr 0.6fr 0.6fr;
		padding: 0.625rem 1rem;
		align-items: center;
		gap: 0.5rem;
	}

	.table-header {
		background: var(--color-bg);
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		color: var(--color-text-muted);
		border-bottom: 1px solid var(--color-border);
	}

	.table-row {
		font-size: 0.8125rem;
		border-bottom: 1px solid var(--color-border);
	}

	.table-row:last-child {
		border-bottom: none;
	}

	.quality-dot {
		display: inline-block;
		width: 8px;
		height: 8px;
		border-radius: 50%;
		background: var(--color-text-subtle);
	}

	.quality-high {
		background: var(--color-success);
	}

	.quality-medium {
		background: var(--color-warning);
	}

	.quality-low {
		background: var(--color-text-subtle);
	}

	.quality-unknown {
		background: var(--color-border);
	}

	/* System health */
	.system-ok {
		display: flex;
		align-items: center;
		gap: 0.75rem;
		padding: 1rem 1.25rem;
	}

	/* Skeleton */
	.admin-skeleton .skeleton {
		background: var(--color-bg-hover);
		border-radius: var(--radius-sm);
		animation: shimmer 1.5s ease-in-out infinite;
	}

	@keyframes shimmer {
		0%, 100% { opacity: 0.5; }
		50% { opacity: 1; }
	}

	/* Tab Navigation */
	.admin-tabs {
		display: flex;
		gap: 0;
		border-bottom: 1px solid var(--color-border);
		margin-bottom: 2rem;
	}

	.admin-tab {
		padding: 0.625rem 1.25rem;
		font-size: 0.875rem;
		font-weight: 500;
		color: var(--color-text-muted);
		background: none;
		border: none;
		border-bottom: 2px solid transparent;
		cursor: pointer;
		transition: color 150ms, border-color 150ms;
		margin-bottom: -1px;
	}

	.admin-tab:hover {
		color: var(--color-text);
	}

	.admin-tab--active {
		color: var(--color-text);
		border-bottom-color: var(--color-text);
	}

	/* User Management */
	.user-controls {
		display: flex;
		gap: 0.75rem;
		align-items: center;
		margin-bottom: 1rem;
	}

	.user-search-wrap {
		position: relative;
		flex: 1;
		max-width: 360px;
	}

	.user-search-icon {
		position: absolute;
		left: 0.75rem;
		top: 50%;
		transform: translateY(-50%);
		color: var(--color-text-muted);
		pointer-events: none;
	}

	.user-search {
		width: 100%;
		padding: 0.5rem 0.75rem 0.5rem 2.25rem;
		font-size: 0.8125rem;
		background: var(--color-bg-elevated);
		border: 1px solid var(--color-border);
		border-radius: 2px;
		color: var(--color-text);
		outline: none;
		transition: border-color 150ms;
	}

	.user-search:focus {
		border-color: var(--color-text-muted);
	}

	.user-search::placeholder {
		color: var(--color-text-subtle);
	}

	.user-tier-filter {
		padding: 0.5rem 0.75rem;
		font-size: 0.8125rem;
		background: var(--color-bg-elevated);
		border: 1px solid var(--color-border);
		border-radius: 2px;
		color: var(--color-text);
		cursor: pointer;
	}

	.user-summary {
		margin-bottom: 0.75rem;
	}

	.user-loading {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		padding: 2rem 0;
		color: var(--color-text-muted);
		font-size: 0.875rem;
	}

	/* User Table */
	.user-table {
		background: var(--color-bg-elevated);
		border: 1px solid var(--color-border);
		border-radius: 2px;
		overflow: hidden;
	}

	.user-table-header,
	.user-table-row {
		display: grid;
		grid-template-columns: 2fr 0.7fr 0.6fr 0.6fr 0.6fr 0.7fr 1fr 1fr 0.8fr;
		padding: 0.625rem 1rem;
		align-items: center;
		gap: 0.5rem;
	}

	.user-table-header {
		background: var(--color-bg);
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		color: var(--color-text-muted);
		border-bottom: 1px solid var(--color-border);
	}

	.user-table-row {
		font-size: 0.8125rem;
		border-bottom: 1px solid var(--color-border);
		transition: background 150ms;
	}

	.user-table-row:last-child {
		border-bottom: none;
	}

	.user-table-row:hover {
		background: var(--color-bg-hover);
	}

	.user-row-updating {
		opacity: 0.6;
	}

	.user-table-empty {
		padding: 2rem 1rem;
		text-align: center;
	}

	.user-email {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.stripe-indicator {
		display: inline-block;
		margin-left: 0.375rem;
		font-size: 0.6875rem;
		font-weight: 600;
		color: var(--color-primary);
	}

	.trial-chip {
		display: inline-block;
		margin-left: 0.375rem;
		padding: 0.0625rem 0.375rem;
		font-size: 0.625rem;
		font-weight: 500;
		letter-spacing: 0.02em;
		color: var(--color-text-muted);
		background: var(--color-bg);
		border: 1px solid var(--color-border);
		border-radius: 2px;
		vertical-align: baseline;
	}

	.trial-chip-expired {
		color: var(--color-danger, #b42318);
		border-color: var(--color-danger, #b42318);
	}

	.user-actions {
		display: inline-flex;
		gap: 0.25rem;
	}

	.btn-trial-grant {
		background: var(--color-bg);
		border: 1px solid var(--color-border);
		border-radius: 2px;
		color: var(--color-text-muted);
		cursor: pointer;
		transition: color 150ms, border-color 150ms;
	}

	.btn-trial-grant:hover:not(:disabled) {
		color: var(--color-text);
		border-color: var(--color-text-muted);
	}

	.btn-trial-grant:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	.profile-link {
		color: var(--color-primary);
		text-decoration: none;
		font-size: 0.8125rem;
	}

	.profile-link:hover {
		text-decoration: underline;
	}

	.btn-xs {
		padding: 0.25rem 0.5rem;
		font-size: 0.6875rem;
		font-weight: 500;
	}

	.btn-tier-toggle {
		background: var(--color-bg);
		border: 1px solid var(--color-border);
		border-radius: 2px;
		color: var(--color-text-muted);
		cursor: pointer;
		transition: color 150ms, border-color 150ms;
	}

	.btn-tier-toggle:hover:not(:disabled) {
		color: var(--color-text);
		border-color: var(--color-text-muted);
	}

	.btn-tier-toggle:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}

	/* Pagination */
	.user-pagination {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 1rem;
		margin-top: 1rem;
	}

	/* Tier Confirm Dialog */
	.tier-confirm-overlay {
		position: fixed;
		inset: 0;
		background: rgba(0, 0, 0, 0.5);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 1000;
	}

	.tier-confirm-dialog {
		background: var(--color-bg-elevated);
		border: 1px solid var(--color-border);
		border-radius: 2px;
		padding: 1.5rem;
		max-width: 420px;
		width: 90%;
	}

	.tier-confirm-dialog .t-subtitle {
		margin-bottom: 0.75rem;
	}

	.tier-confirm-dialog .t-body {
		margin-bottom: 0.5rem;
		line-height: 1.6;
	}

	.tier-confirm-dialog .t-caption {
		margin-bottom: 1rem;
		display: block;
	}

	.tier-confirm-actions {
		display: flex;
		gap: 0.5rem;
		justify-content: flex-end;
	}

	/* Responsive */
	@media (max-width: 768px) {
		.stats-grid-3,
		.stats-grid-4,
		.stats-grid-5 {
			grid-template-columns: repeat(2, 1fr);
		}

		.distributions-row {
			grid-template-columns: 1fr;
		}

		.metrics-row {
			flex-direction: column;
			gap: 1rem;
		}

		.table-header,
		.table-row {
			grid-template-columns: 1.5fr 1fr 1fr 0.5fr;
		}

		.table-header span:nth-child(4),
		.table-row span:nth-child(4),
		.table-header span:nth-child(6),
		.table-row span:nth-child(6),
		.table-header span:nth-child(7),
		.table-row span:nth-child(7) {
			display: none;
		}

		.user-controls {
			flex-direction: column;
			align-items: stretch;
		}

		.user-search-wrap {
			max-width: none;
		}

		.user-table-header,
		.user-table-row {
			grid-template-columns: 2fr 0.6fr 0.6fr 0.8fr;
		}

		/* Hide less important columns on mobile: Verified, Claims, Cost, Profile, Joined */
		.user-table-header span:nth-child(3),
		.user-table-row span:nth-child(3),
		.user-table-header span:nth-child(5),
		.user-table-row span:nth-child(5),
		.user-table-header span:nth-child(6),
		.user-table-row span:nth-child(6),
		.user-table-header span:nth-child(7),
		.user-table-row span:nth-child(7),
		.user-table-header span:nth-child(8),
		.user-table-row span:nth-child(8) {
			display: none;
		}
	}
`;
